import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useLang } from '../context/LanguageContext';

const FRUIT_TYPES = ['Orange', 'Lemon', 'Grapefruit', 'Mandarin', 'Lime', 'Tangerine', 'Clementine', 'Kumquat'];
const QUALITY_CLASSES = ['Extra', 'Class I', 'Class II'];

export default function Settings() {
  const { t } = useLang();
  const [tab, setTab] = useState('products');
  const [users, setUsers] = useState([]);
  const [packagers, setPackagers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });

  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'inspector' });
  const [packagerForm, setPackagerForm] = useState({ packager_number: '', name: '' });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [orphanData, setOrphanData] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const OECD_DEFAULTS = { 'Extra': [5, 2.5, 0.5], 'Class I': [10, 5, 1], 'Class II': [10, 5, 2] };
  const [productForm, setProductForm] = useState({
    product_code: '', fruit_type: '', size_code: '', diameter_range: '',
    pieces_per_box: '', quality_class: 'Class I', minor_tolerance: '10', major_tolerance: '5', critical_tolerance: '1', description: ''
  });

  useEffect(() => { fetchUsers(); fetchPackagers(); fetchProducts(); }, []);

  const fetchUsers = async () => { try { setUsers((await api.get('/admin/users')).data); } catch (e) {} };
  const fetchPackagers = async () => { try { setPackagers((await api.get('/admin/packagers')).data); } catch (e) {} };
  const fetchProducts = async () => { try { setProducts((await api.get('/admin/products')).data); } catch (e) {} };

  const showMsg = (text, type = 'success') => { setMessage({ text, type }); setTimeout(() => setMessage({ text: '', type: '' }), 3000); };
  const resetForm = () => { setShowForm(false); setEditing(null); };
  const switchTab = (t) => { setTab(t); resetForm(); };

  // Product CRUD
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/products/${editing.id}`, productForm); showMsg(t('product_updated')); }
      else { await api.post('/admin/products', productForm); showMsg(t('product_created')); }
      resetForm(); setProductForm({ product_code: '', fruit_type: '', size_code: '', diameter_range: '', pieces_per_box: '', quality_class: 'Class I', minor_tolerance: '10', major_tolerance: '5', critical_tolerance: '1', description: '' }); fetchProducts();
    } catch (err) { showMsg(err.response?.data?.error || t('error'), 'error'); }
  };
  const editProduct = (p) => { setEditing(p); setProductForm({ product_code: p.product_code, fruit_type: p.fruit_type, size_code: p.size_code, diameter_range: p.diameter_range || '', pieces_per_box: p.pieces_per_box, quality_class: p.quality_class, minor_tolerance: p.minor_tolerance || '10', major_tolerance: p.major_tolerance || '5', critical_tolerance: p.critical_tolerance || '1', description: p.description || '' }); setShowForm(true); };
  const deleteProduct = async (id) => { if (!window.confirm(t('deactivate_confirm'))) return; try { await api.delete(`/admin/products/${id}`); showMsg(t('product_deactivated')); fetchProducts(); } catch (err) { showMsg(t('error'), 'error'); } };

  // User CRUD
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { const p = { username: userForm.username, role: userForm.role }; if (userForm.password) p.password = userForm.password; await api.put(`/admin/users/${editing.id}`, p); showMsg(t('user_updated')); }
      else { await api.post('/admin/users', userForm); showMsg(t('user_created')); }
      resetForm(); setUserForm({ username: '', password: '', role: 'inspector' }); fetchUsers();
    } catch (err) { showMsg(err.response?.data?.error || t('error'), 'error'); }
  };
  const editUser = (u) => { setEditing(u); setUserForm({ username: u.username, password: '', role: u.role }); setShowForm(true); };
  const deleteUser = async (id) => { if (!window.confirm(t('delete_user_confirm'))) return; try { await api.delete(`/admin/users/${id}`); showMsg(t('user_deleted')); fetchUsers(); } catch (err) { showMsg(err.response?.data?.error || t('error'), 'error'); } };

  // Packager CRUD
  const handlePackagerSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/packagers/${editing.id}`, packagerForm); showMsg(t('packager_updated')); }
      else { await api.post('/admin/packagers', packagerForm); showMsg(t('packager_created_msg')); }
      resetForm(); setPackagerForm({ packager_number: '', name: '' }); fetchPackagers();
    } catch (err) { showMsg(err.response?.data?.error || t('error'), 'error'); }
  };
  const editPackager = (p) => { setEditing(p); setPackagerForm({ packager_number: p.packager_number, name: p.name }); setShowForm(true); };
  const togglePackagerStatus = async (p) => {
    const newActive = !p.active;
    const msg = newActive ? t('activate_packager_confirm') : t('deactivate_packager_confirm');
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/admin/packagers/${p.id}/status`, { active: newActive });
      showMsg(newActive ? t('packager_activated') : t('packager_deactivated'));
      fetchPackagers();
    } catch (err) { showMsg(err.response?.data?.error || t('error'), 'error'); }
  };
  const wipePackager = async (p) => {
    if (!window.confirm(t('wipe_packager_confirm', { name: p.name, number: p.packager_number }))) return;
    if (!window.confirm(t('wipe_packager_final'))) return;
    try {
      await api.delete(`/admin/packagers/${p.id}`);
      showMsg(t('packager_wiped'));
      fetchPackagers();
    } catch (err) { showMsg(err.response?.data?.error || t('error'), 'error'); }
  };

  // Orphaned inspection cleanup
  const checkOrphans = async () => {
    try {
      const res = await api.get('/admin/orphaned-inspections');
      setOrphanData(res.data);
    } catch (err) { showMsg(t('error'), 'error'); }
  };
  const cleanupOrphans = async () => {
    if (!window.confirm(t('cleanup_orphans_confirm', { count: orphanData.total_inspections }))) return;
    if (!window.confirm(t('wipe_packager_final'))) return;
    setCleaningUp(true);
    try {
      const res = await api.delete('/admin/orphaned-inspections');
      showMsg(t('cleanup_orphans_done', { count: res.data.deleted_inspections }));
      setOrphanData(null);
    } catch (err) { showMsg(err.response?.data?.error || t('error'), 'error'); }
    finally { setCleaningUp(false); }
  };

  // Import packagers from Excel
  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/admin/packagers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      showMsg(res.data.message);
      setImportFile(null);
      fetchPackagers();
    } catch (err) {
      showMsg(err.response?.data?.error || t('error'), 'error');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('token');
    window.open(`/api/admin/packagers/template?token=${token}`, '_blank');
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #fff7ed 0%, #f8fafc 100%)' }}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-5 fade-in">⚙️ {t('settings_title')}</h1>

        {message.text && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-medium fade-in ${message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>{message.text}</div>
        )}

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-1 touch-scroll">
          {[
            { key: 'products', label: `🍊 ${t('products_tab')}`, count: products.length },
            { key: 'users', label: `👤 ${t('users_tab')}`, count: users.length },
            { key: 'packagers', label: `📦 ${t('packagers_tab')}`, count: packagers.length }
          ].map(tb => (
            <button key={tb.key} onClick={() => switchTab(tb.key)}
              className={`px-5 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all touch-active ${
                tab === tb.key ? 'bg-orange-500 text-white shadow-md' : 'bg-white text-gray-600 active:bg-orange-50 border border-orange-100'
              }`}>
              {tb.label} <span className="ml-1 text-xs opacity-75">({tb.count})</span>
            </button>
          ))}
        </div>

        {/* ====== PRODUCTS TAB ====== */}
        {tab === 'products' && (
          <div className="fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-700">{t('product_catalog')}</h2>
              <button onClick={() => { setShowForm(true); setEditing(null); setProductForm({ product_code: '', fruit_type: '', size_code: '', diameter_range: '', pieces_per_box: '', quality_class: 'Class I', minor_tolerance: '10', major_tolerance: '5', critical_tolerance: '1', description: '' }); }}
                className="px-5 py-3 bg-green-500 text-white rounded-xl font-semibold text-sm active:bg-green-600 shadow-md touch-active">
                {t('add_product')}
              </button>
            </div>

            {showForm && (
              <form onSubmit={handleProductSubmit} className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6 fade-in">
                <h3 className="font-bold text-gray-800 mb-4">{editing ? t('edit_product') : t('new_product')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">{t('product_code')}</label>
                    <input type="text" value={productForm.product_code} onChange={(e) => setProductForm({ ...productForm, product_code: e.target.value })}
                      placeholder="e.g., ORG-1" className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">{t('fruit_type')}</label>
                    <input type="text" list="fruitTypeList" value={productForm.fruit_type} onChange={(e) => setProductForm({ ...productForm, fruit_type: e.target.value })}
                      placeholder="e.g., Orange" className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required />
                    <datalist id="fruitTypeList">
                      {FRUIT_TYPES.map(f => <option key={f} value={f} />)}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">{t('size_code')}</label>
                    <input type="text" value={productForm.size_code} onChange={(e) => setProductForm({ ...productForm, size_code: e.target.value })}
                      placeholder="e.g., 3" className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">{t('diameter_range')}</label>
                    <input type="text" value={productForm.diameter_range} onChange={(e) => setProductForm({ ...productForm, diameter_range: e.target.value })}
                      placeholder="e.g., 81-92mm" className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">{t('pieces_per_box')}</label>
                    <input type="number" min="1" value={productForm.pieces_per_box} onChange={(e) => setProductForm({ ...productForm, pieces_per_box: e.target.value })}
                      placeholder="e.g., 100" className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">{t('quality_class')}</label>
                    <select value={productForm.quality_class} onChange={(e) => {
                      const qc = e.target.value;
                      const [minT, majT, critT] = OECD_DEFAULTS[qc] || OECD_DEFAULTS['Class I'];
                      setProductForm({ ...productForm, quality_class: qc, minor_tolerance: String(minT), major_tolerance: String(majT), critical_tolerance: String(critT) });
                    }}
                      className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 bg-white">
                      {QUALITY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Tolerance Settings */}
                <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                  <label className="block text-xs font-bold text-blue-700 mb-3">{t('tolerance_settings')}</label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-yellow-600 mb-1">{t('minor_tolerance')}</label>
                      <input type="number" step="0.1" min="0" max="100" value={productForm.minor_tolerance}
                        onChange={(e) => setProductForm({ ...productForm, minor_tolerance: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl focus:outline-none focus:border-yellow-500 text-center font-bold" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-red-600 mb-1">{t('major_tolerance')}</label>
                      <input type="number" step="0.1" min="0" max="100" value={productForm.major_tolerance}
                        onChange={(e) => setProductForm({ ...productForm, major_tolerance: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-red-300 rounded-xl focus:outline-none focus:border-red-500 text-center font-bold" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-purple-600 mb-1">{t('critical_tolerance')}</label>
                      <input type="number" step="0.1" min="0" max="100" value={productForm.critical_tolerance}
                        onChange={(e) => setProductForm({ ...productForm, critical_tolerance: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:outline-none focus:border-purple-500 text-center font-bold" />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-bold text-gray-600 mb-1">{t('description')}</label>
                  <input type="text" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    placeholder="e.g., Navel Orange Size 3" className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" />
                </div>
                <div className="flex space-x-3 mt-4">
                  <button type="submit" className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold active:bg-orange-600 touch-active">{editing ? t('update') : t('create')}</button>
                  <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-semibold active:bg-gray-200 touch-active">{t('cancel')}</button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-2xl shadow-md border border-orange-100 overflow-hidden">
              <div className="touch-scroll">
                <table className="w-full text-sm" style={{ minWidth: '800px' }}>
                  <thead>
                    <tr className="border-b-2 border-orange-100 bg-orange-50">
                      <th className="text-left py-3 px-3 font-bold text-gray-600">{t('product_code')}</th>
                      <th className="text-left py-3 px-3 font-bold text-gray-600">{t('fruit')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('size_code')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('diameter')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('pieces_per_box')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('quality_class')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('status')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id} className={`border-b border-gray-50 transition-colors ${p.active ? 'hover:bg-orange-50' : 'bg-gray-50 opacity-60'}`}>
                        <td className="py-3 px-3 font-semibold">{p.product_code}</td>
                        <td className="py-3 px-3">{p.fruit_type}</td>
                        <td className="py-3 px-3 text-center">{p.size_code}</td>
                        <td className="py-3 px-3 text-center text-gray-500">{p.diameter_range}</td>
                        <td className="py-3 px-3 text-center font-bold text-orange-600">{p.pieces_per_box}</td>
                        <td className="py-3 px-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${p.quality_class === 'Extra' ? 'bg-purple-100 text-purple-700' : p.quality_class === 'Class I' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{p.quality_class}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>{p.active ? t('active') : t('inactive')}</span>
                        </td>
                        <td className="py-3 px-3 text-center space-x-1">
                          <button onClick={() => editProduct(p)} className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold active:bg-blue-100 touch-active">{t('edit')}</button>
                          {p.active && <button onClick={() => deleteProduct(p.id)} className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-semibold active:bg-red-100 touch-active">{t('deactivate')}</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ====== USERS TAB ====== */}
        {tab === 'users' && (
          <div className="fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-700">{t('users_tab')}</h2>
              <button onClick={() => { setShowForm(true); setEditing(null); setUserForm({ username: '', password: '', role: 'inspector' }); }}
                className="px-5 py-3 bg-green-500 text-white rounded-xl font-semibold text-sm active:bg-green-600 shadow-md touch-active">{t('add_user')}</button>
            </div>

            {showForm && (
              <form onSubmit={handleUserSubmit} className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6 fade-in">
                <h3 className="font-bold text-gray-800 mb-4">{editing ? t('edit_user') : t('new_user')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input type="text" value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                    placeholder={t('username')} className="px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required />
                  <input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder={editing ? t('new_password') : t('password')} className="px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required={!editing} />
                  <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 bg-white">
                    <option value="inspector">Inspector</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex space-x-3 mt-4">
                  <button type="submit" className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold active:bg-orange-600 touch-active">{editing ? t('update') : t('create')}</button>
                  <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-semibold active:bg-gray-200 touch-active">{t('cancel')}</button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-2xl shadow-md border border-orange-100 overflow-hidden">
              <div className="touch-scroll">
                <table className="w-full text-sm" style={{ minWidth: '500px' }}>
                  <thead>
                    <tr className="border-b-2 border-orange-100 bg-orange-50">
                      <th className="text-left py-3 px-4 font-bold text-gray-600">{t('username')}</th>
                      <th className="text-center py-3 px-4 font-bold text-gray-600">{t('role')}</th>
                      <th className="text-center py-3 px-4 font-bold text-gray-600">{t('created')}</th>
                      <th className="text-center py-3 px-4 font-bold text-gray-600">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-orange-50 transition-colors">
                        <td className="py-3 px-4 font-semibold">{u.username}</td>
                        <td className="py-3 px-4 text-center"><span className={`px-3 py-1 rounded-full text-xs font-bold ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{u.role}</span></td>
                        <td className="py-3 px-4 text-center text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4 text-center space-x-1">
                          <button onClick={() => editUser(u)} className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold active:bg-blue-100 touch-active">{t('edit')}</button>
                          <button onClick={() => deleteUser(u.id)} className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-semibold active:bg-red-100 touch-active">{t('delete')}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ====== PACKAGERS TAB ====== */}
        {tab === 'packagers' && (
          <div className="fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-700">{t('packagers_tab')}</h2>
              <button onClick={() => { setShowForm(true); setEditing(null); setPackagerForm({ packager_number: '', name: '' }); }}
                className="px-5 py-3 bg-green-500 text-white rounded-xl font-semibold text-sm active:bg-green-600 shadow-md touch-active">{t('add_packager')}</button>
            </div>

            {/* Excel Import Section */}
            <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6">
              <h3 className="font-bold text-gray-800 mb-3">📥 {t('import_packagers')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('import_packagers_desc')}</p>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={downloadTemplate}
                  className="px-4 py-3 bg-blue-50 text-blue-600 rounded-xl font-semibold text-sm active:bg-blue-100 border border-blue-200 touch-active">
                  📄 {t('download_template')}
                </button>
                <label className="px-4 py-3 bg-orange-50 text-orange-600 rounded-xl font-semibold text-sm cursor-pointer active:bg-orange-100 border border-orange-200 touch-active">
                  📎 {importFile ? importFile.name : t('choose_file')}
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); }} />
                </label>
                {importFile && (
                  <button onClick={handleImport} disabled={importing}
                    className="px-5 py-3 bg-green-500 text-white rounded-xl font-semibold text-sm active:bg-green-600 shadow-md touch-active disabled:opacity-50">
                    {importing ? t('importing') : t('import_now')}
                  </button>
                )}
              </div>
              {importResult && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl fade-in">
                  <p className="font-semibold text-green-700">
                    ✅ {t('imported')}: {importResult.imported} | ⏭ {t('skipped')}: {importResult.skipped} | 📊 {t('total')}: {importResult.total}
                  </p>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2 text-sm text-red-600">
                      {importResult.errors.map((err, i) => (
                        <p key={i}>{t('row')} {err.row}: {err.reason}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Orphaned Data Cleanup */}
            <div className="bg-white rounded-2xl shadow-md p-5 border border-red-100 mb-6">
              <h3 className="font-bold text-gray-800 mb-2">🧹 {t('cleanup_title')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('cleanup_desc')}</p>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={checkOrphans}
                  className="px-5 py-3 bg-orange-50 text-orange-700 rounded-xl font-semibold text-sm border border-orange-200 active:bg-orange-100 touch-active">
                  🔍 {t('cleanup_scan')}
                </button>
                {orphanData && orphanData.total_inspections > 0 && (
                  <button onClick={cleanupOrphans} disabled={cleaningUp}
                    className="px-5 py-3 bg-red-500 text-white rounded-xl font-semibold text-sm active:bg-red-600 shadow-md touch-active disabled:opacity-50">
                    {cleaningUp ? '...' : `🗑️ ${t('cleanup_delete_all')}`}
                  </button>
                )}
              </div>
              {orphanData && (
                <div className="mt-4 fade-in">
                  {orphanData.total_inspections === 0 ? (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 font-semibold">
                      ✅ {t('cleanup_none')}
                    </div>
                  ) : (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <p className="font-bold text-red-700 mb-2">
                        ⚠️ {t('cleanup_found', { count: orphanData.total_inspections, packers: orphanData.orphaned_packers.length })}
                      </p>
                      <div className="space-y-1">
                        {orphanData.orphaned_packers.map((o, i) => (
                          <div key={i} className="flex justify-between text-sm px-3 py-2 bg-white rounded-lg">
                            <span className="font-semibold text-gray-700">{o.packager_number}</span>
                            <span className="text-gray-500">{o.count} {t('boxes')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {showForm && (
              <form onSubmit={handlePackagerSubmit} className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6 fade-in">
                <h3 className="font-bold text-gray-800 mb-4">{editing ? t('edit_packager') : t('new_packager')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" value={packagerForm.packager_number} onChange={(e) => setPackagerForm({ ...packagerForm, packager_number: e.target.value })}
                    placeholder={`${t('packager_number')} (e.g., PACK006)`} className="px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required />
                  <input type="text" value={packagerForm.name} onChange={(e) => setPackagerForm({ ...packagerForm, name: e.target.value })}
                    placeholder={t('packager_name')} className="px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400" required />
                </div>
                <div className="flex space-x-3 mt-4">
                  <button type="submit" className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold active:bg-orange-600 touch-active">{editing ? t('update') : t('create')}</button>
                  <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-semibold active:bg-gray-200 touch-active">{t('cancel')}</button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-2xl shadow-md border border-orange-100 overflow-hidden">
              <div className="touch-scroll">
                <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                  <thead>
                    <tr className="border-b-2 border-orange-100 bg-orange-50">
                      <th className="text-left py-3 px-4 font-bold text-gray-600">{t('packager_number')}</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-600">{t('name')}</th>
                      <th className="text-center py-3 px-4 font-bold text-gray-600">{t('status')}</th>
                      <th className="text-center py-3 px-4 font-bold text-gray-600">{t('created')}</th>
                      <th className="text-center py-3 px-4 font-bold text-gray-600">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packagers.map(p => (
                      <tr key={p.id} className={`border-b border-gray-50 hover:bg-orange-50 transition-colors ${!p.active ? 'opacity-50' : ''}`}>
                        <td className="py-3 px-4 font-semibold">{p.packager_number}</td>
                        <td className="py-3 px-4">{p.name}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                            {p.active ? t('active') : t('inactive')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex flex-wrap justify-center gap-1">
                            <button onClick={() => editPackager(p)} className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold active:bg-blue-100 touch-active">{t('edit')}</button>
                            <button onClick={() => togglePackagerStatus(p)} className={`px-3 py-2 rounded-lg text-xs font-semibold active:opacity-80 touch-active ${p.active ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700'}`}>
                              {p.active ? t('deactivate') : t('activate')}
                            </button>
                            <button onClick={() => wipePackager(p)} className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-semibold active:bg-red-100 touch-active">{t('delete_wipe')}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
